import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Header } from "@/components/Header";
import { NoBusinessGuard } from "@/components/NoBusinessGuard";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { ChartLineUp, Lightning, ArrowRight } from "@phosphor-icons/react";
import { TemplateManager } from "@/components/TemplateManager";
import { UsageWarningBanner } from "@/components/UsageWarningBanner";
import { UpgradeModal } from "@/components/UpgradeModal";
import { AlwaysIncludeIndicator } from "@/components/AlwaysIncludeIndicator";
import { PLATFORMS, AWARENESS_STAGES, LOADING_MESSAGES } from "@/components/sequence/sequenceConstants";

export default function SequencePlannerPage() {
  const { api, refreshUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [topicOrNiche, setTopicOrNiche] = useState("");
  const [audience, setAudience] = useState("");
  const [awarenessStage, setAwarenessStage] = useState("cold");
  const [endGoal, setEndGoal] = useState("");
  const [platform, setPlatform] = useState("");
  const [contentCount, setContentCount] = useState(7);
  const [researchText, setResearchText] = useState("");
  const [aiModel, setAiModel] = useState("");
  const [availableModels, setAvailableModels] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [loadingMsg, setLoadingMsg] = useState("");
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [result, setResult] = useState(null);
  const [alwaysIncludeFiles, setAlwaysIncludeFiles] = useState([]);

  useEffect(() => {
    const prefill = location.state?.prefill;
    if (!prefill) return;
    if (prefill.topic_or_niche) setTopicOrNiche(prefill.topic_or_niche);
    if (prefill.audience) setAudience(prefill.audience);
    if (prefill.end_goal) setEndGoal(prefill.end_goal);
    if (prefill.platform) setPlatform(prefill.platform);
    if (prefill.research_text) setResearchText(prefill.research_text);
    if (prefill.ai_model) setAiModel(prefill.ai_model);
  }, [location.state]);

  useEffect(() => {
    const fetchModels = async () => {
      try {
        const resp = await api.get("/models/available");
        setAvailableModels(resp.data.models || []);
        if (resp.data.models?.length > 0) setAiModel(resp.data.models[0]);
      } catch (e) { console.error("Failed to fetch models:", e); }
    };
    fetchModels();
  }, [api]);

  // Load profile defaults on mount
  useEffect(() => {
    const loadDefaults = async () => {
      try {
        const resp = await api.get("/profile");
        const p = resp.data;
        setAudience(prev => prev || p.default_audience || "");
        setAiModel(prev => prev || p.default_ai_model || "");
        setAwarenessStage(prev => prev === "cold" ? (p.default_awareness_stage || prev) : prev);
        setContentCount(prev => prev === 7 ? (p.default_content_count || prev) : prev);
      } catch (e) { console.error("Failed to load defaults:", e); }
    };
    loadDefaults();
  }, [api]);

  useEffect(() => {
    if (!generating) return;
    let idx = 0;
    setLoadingMsg(LOADING_MESSAGES[0]);
    const interval = setInterval(() => {
      idx = (idx + 1) % LOADING_MESSAGES.length;
      setLoadingMsg(LOADING_MESSAGES[idx]);
    }, 3000);
    return () => clearInterval(interval);
  }, [generating]);

  const handleGenerate = async (e) => {
    e.preventDefault();
    if (!topicOrNiche || !audience || !endGoal || !platform || !aiModel) {
      toast.error("Please fill in all required fields");
      return;
    }

    setGenerating(true);
    setResult(null);

    try {
      const resp = await api.post("/sequence/plan", {
        topic_or_niche: topicOrNiche,
        audience,
        awareness_stage: awarenessStage,
        end_goal: endGoal,
        platform,
        content_count: contentCount,
        ai_model: aiModel,
        research_text: researchText,
      });
      setResult(resp.data);
      toast.success("Sequence planned! +20 XP");
      refreshUser();
    } catch (err) {
      if (err.response?.status === 429) setShowUpgrade(true);
      toast.error(err.response?.data?.detail || "Sequence planning failed.");
    } finally {
      setGenerating(false);
    }
  };

  const launchPiece = (piece) => {
    navigate("/generate", {
      state: {
        prefill: {
          topic: piece.title,
          research_material: piece.angle + "\n\nStrategic purpose: " + piece.purpose,
          content_type: piece.type || "Thought Leadership",
          platform: result?.platform || "LinkedIn",
          audience: result?.audience || "",
          goal: result?.end_goal || "Build trust",
          tone_variations: ["Direct", "Insightful"],
        }
      }
    });
  };

  return (
    <NoBusinessGuard>
    <div className="min-h-screen bg-[var(--bg-base)]">
      <Header />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8 space-y-8">
        <div className="flex items-center gap-3">
          <ChartLineUp size={28} className="text-[#FF4400]" />
          <div>
            <h1 className="font-['Cormorant_Garamond'] text-2xl sm:text-3xl font-bold text-[var(--text-primary)] tracking-tight" data-testid="sequence-page-title">Sequence Planner</h1>
            <p className="text-sm text-[var(--text-muted)] mt-0.5">Plan a full content arc that moves your audience toward a specific goal</p>
          </div>
        </div>

        <UsageWarningBanner />

        <form onSubmit={handleGenerate} className="space-y-6" data-testid="sequence-form">
          <div>
            <label className="block font-['JetBrains_Mono'] text-xs uppercase tracking-[0.2em] text-[var(--text-muted)] mb-2">
              Topic / Niche <span className="text-[#FF4400]">*</span>
            </label>
            <Input
              value={topicOrNiche}
              onChange={(e) => setTopicOrNiche(e.target.value)}
              placeholder="e.g., content marketing strategy, thought leadership, authority positioning"
              required
              className="bg-[var(--bg-card)] border-[var(--border-strong)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:ring-1 focus:ring-[#FF4400] h-11"
              data-testid="sequence-topic-input"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block font-['JetBrains_Mono'] text-xs uppercase tracking-[0.2em] text-[var(--text-muted)] mb-2">
                Audience <span className="text-[#FF4400]">*</span>
              </label>
              <Input
                value={audience}
                onChange={(e) => setAudience(e.target.value)}
                placeholder="Who is this sequence for?"
                required
                className="bg-[var(--bg-card)] border-[var(--border-strong)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:ring-1 focus:ring-[#FF4400] h-11"
                data-testid="sequence-audience-input"
              />
            </div>
            <div>
              <label className="block font-['JetBrains_Mono'] text-xs uppercase tracking-[0.2em] text-[var(--text-muted)] mb-2">
                Platform <span className="text-[#FF4400]">*</span>
              </label>
              <Select value={platform} onValueChange={setPlatform}>
                <SelectTrigger className="bg-[var(--bg-card)] border-[var(--border-strong)] text-[var(--text-primary)] focus:ring-1 focus:ring-[#FF4400] h-11" data-testid="sequence-platform-select">
                  <SelectValue placeholder="Select platform" />
                </SelectTrigger>
                <SelectContent className="bg-[var(--bg-raised)] border-[var(--border-strong)]">
                  {PLATFORMS.map((p) => (
                    <SelectItem key={p} value={p} className="text-[var(--text-secondary)] focus:bg-[var(--accent-subtle)] focus:text-white">{p}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Awareness stage */}
          <div>
            <label className="block font-['JetBrains_Mono'] text-xs uppercase tracking-[0.2em] text-[var(--text-muted)] mb-3">
              Audience Awareness Stage <span className="text-[#FF4400]">*</span>
            </label>
            <div className="grid grid-cols-3 gap-3" data-testid="sequence-awareness-grid">
              {AWARENESS_STAGES.map((stage) => (
                <button
                  key={stage.value}
                  type="button"
                  onClick={() => setAwarenessStage(stage.value)}
                  data-testid={`sequence-awareness-${stage.value}`}
                  className={`px-4 py-3 rounded-md border text-left transition-all duration-200 ${
                    awarenessStage === stage.value
                      ? "bg-[var(--accent-subtle)] border-[#FF4400] text-[#FF4400]"
                      : "bg-[var(--bg-card)] border-[var(--border-strong)] text-[var(--text-secondary)] hover:border-[var(--border-strong)]"
                  }`}
                >
                  <div className="text-sm font-medium">{stage.label}</div>
                  <div className="text-[10px] opacity-60 mt-0.5">{stage.description}</div>
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block font-['JetBrains_Mono'] text-xs uppercase tracking-[0.2em] text-[var(--text-muted)] mb-2">
              End Goal <span className="text-[#FF4400]">*</span>
            </label>
            <Input
              value={endGoal}
              onChange={(e) => setEndGoal(e.target.value)}
              placeholder="e.g., Book a strategy call, Download the lead magnet, Join the email list"
              required
              className="bg-[var(--bg-card)] border-[var(--border-strong)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:ring-1 focus:ring-[#FF4400] h-11"
              data-testid="sequence-goal-input"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block font-['JetBrains_Mono'] text-xs uppercase tracking-[0.2em] text-[var(--text-muted)] mb-2">
                Number of Pieces
              </label>
              <Select value={String(contentCount)} onValueChange={(v) => setContentCount(Number(v))}>
                <SelectTrigger className="bg-[var(--bg-card)] border-[var(--border-strong)] text-[var(--text-primary)] focus:ring-1 focus:ring-[#FF4400] h-11" data-testid="sequence-count-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[var(--bg-raised)] border-[var(--border-strong)]">
                  {[5, 7, 10, 12, 15].map((n) => (
                    <SelectItem key={n} value={String(n)} className="text-[var(--text-secondary)] focus:bg-[var(--accent-subtle)] focus:text-white">{n} pieces</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block font-['JetBrains_Mono'] text-xs uppercase tracking-[0.2em] text-[var(--text-muted)] mb-2">
                AI Model <span className="text-[#FF4400]">*</span>
              </label>
              <Select value={aiModel} onValueChange={setAiModel}>
                <SelectTrigger className="bg-[var(--bg-card)] border-[var(--border-strong)] text-[var(--text-primary)] focus:ring-1 focus:ring-[#FF4400] h-11" data-testid="sequence-model-select">
                  <SelectValue placeholder="Select model" />
                </SelectTrigger>
                <SelectContent className="bg-[var(--bg-raised)] border-[var(--border-strong)]">
                  {availableModels.map((m) => (
                    <SelectItem key={m} value={m} className="text-[var(--text-secondary)] focus:bg-[var(--accent-subtle)] focus:text-white">{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <label className="block font-['JetBrains_Mono'] text-xs uppercase tracking-[0.2em] text-[var(--text-muted)] mb-2">
              Additional Context (Optional)
            </label>
            <Textarea
              value={researchText}
              onChange={(e) => setResearchText(e.target.value)}
              placeholder="Any context about your positioning, offer, or what you want this sequence to achieve..."
              className="bg-[var(--bg-card)] border-[var(--border-strong)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:ring-1 focus:ring-[#FF4400] min-h-[80px] resize-y"
              data-testid="sequence-context-input"
            />
          </div>

          <TemplateManager
            templateType="sequence"
            currentValues={{
              content_type: "Content Sequence",
              platform,
              audience,
              goal: endGoal,
              tone_variations: [],
              ai_model: aiModel,
              awareness_stage: awarenessStage,
              content_count: contentCount,
            }}
            onLoad={(template) => {
              if (template.platform) setPlatform(template.platform);
              if (template.audience) setAudience(template.audience);
              if (template.goal) setEndGoal(template.goal);
              if (template.awareness_stage) setAwarenessStage(template.awareness_stage);
              if (template.content_count) setContentCount(template.content_count);
              if (template.ai_model && availableModels.includes(template.ai_model)) setAiModel(template.ai_model);
            }}
          />

          <AlwaysIncludeIndicator onFilesLoaded={setAlwaysIncludeFiles} />

          <button
            type="submit"
            disabled={generating || availableModels.length === 0}
            className="w-full bg-[#FF4400] hover:bg-[#FF6633] text-white font-semibold h-14 rounded-md text-base transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_0_20px_rgba(255,68,0,0.4)] disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="sequence-submit-button"
          >
            {generating ? (
              <>
                <ChartLineUp size={20} className="animate-pulse" />
                {loadingMsg}
              </>
            ) : (
              <>
                <Lightning size={20} weight="fill" />
                Plan Content Sequence
              </>
            )}
          </button>
        </form>

        {/* Results */}
        <AnimatePresence>
          {result && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-4"
              data-testid="sequence-results"
            >
              {result.sequence_logic && (
                <div className="bg-[var(--bg-card)] border border-[#FF4400]/20 rounded-lg p-6" data-testid="sequence-logic-block">
                  <h3 className="font-['JetBrains_Mono'] text-xs uppercase tracking-[0.2em] text-[#FF4400] mb-2">Sequence Logic</h3>
                  <p className="text-[var(--text-secondary)] text-sm leading-relaxed">{result.sequence_logic}</p>
                </div>
              )}

              {result.pieces.map((piece, idx) => (
                <motion.div
                  key={`piece-${piece.type || "item"}-${idx}`}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.06 }}
                  className="bg-[var(--bg-card)] border border-[var(--border)] rounded-lg p-6 hover:border-[var(--border-strong)] transition-colors"
                  data-testid={`sequence-piece-${idx}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <span className="font-['JetBrains_Mono'] text-xs text-[var(--success)] opacity-60">
                          {String(idx + 1).padStart(2, "0")}
                        </span>
                        {piece.type && (
                          <span className="px-2 py-0.5 rounded text-xs bg-[var(--accent-subtle)] text-[#FF4400]">{piece.type}</span>
                        )}
                      </div>
                      <h3 className="text-[var(--text-primary)] font-semibold font-['Cormorant_Garamond'] text-lg mb-3">{piece.title}</h3>

                      {piece.hook && (
                        <div className="mb-3">
                          <p className="text-xs font-['JetBrains_Mono'] uppercase tracking-[0.15em] text-[var(--text-muted)] mb-1">Hook</p>
                          <p className="text-[var(--text-secondary)] text-sm italic">"{piece.hook}"</p>
                        </div>
                      )}
                      {piece.purpose && (
                        <div className="mb-3">
                          <p className="text-xs font-['JetBrains_Mono'] uppercase tracking-[0.15em] text-[var(--text-muted)] mb-1">Strategic Purpose</p>
                          <p className="text-[var(--text-secondary)] text-sm">{piece.purpose}</p>
                        </div>
                      )}
                      {piece.angle && (
                        <div className="mb-3">
                          <p className="text-xs font-['JetBrains_Mono'] uppercase tracking-[0.15em] text-[var(--text-muted)] mb-1">Core Angle</p>
                          <p className="text-[var(--text-secondary)] text-sm">{piece.angle}</p>
                        </div>
                      )}
                      {piece.bridge && idx < result.pieces.length - 1 && (
                        <div className="flex items-center gap-2 mt-3 pt-3 border-t border-[var(--border)]">
                          <ArrowRight size={14} className="text-[var(--text-muted)] shrink-0" />
                          <p className="text-xs text-[var(--text-muted)]">{piece.bridge}</p>
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => launchPiece(piece)}
                      className="shrink-0 flex items-center gap-2 px-4 py-2 rounded-md bg-[#FF4400] text-white text-sm font-medium hover:bg-[#FF6633] transition-all duration-200 hover:-translate-y-0.5"
                      data-testid={`sequence-generate-${idx}`}
                    >
                      <Lightning size={14} weight="fill" />
                      Generate
                    </button>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
    <UpgradeModal isOpen={showUpgrade} onClose={() => setShowUpgrade(false)} trigger="plan sequence" />
    </NoBusinessGuard>
  );
}
